import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Business as BusinessIcon,
  Assignment as DocumentIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Gavel as LegalIcon,
  GetApp as DownloadIcon,
  Search as SearchIcon,
  AccountBalance as BankIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const commercialVsResidential = [
  {
    aspect: 'Transaction Timeline',
    residential: '30-45 days',
    commercial: '60-120+ days',
    notes: 'Commercial requires extensive due diligence and complex financing'
  },
  {
    aspect: 'Due Diligence Period',
    residential: '10-21 days',
    commercial: '60-180 days',
    notes: 'Detailed financial, legal, and physical property analysis required'
  },
  {
    aspect: 'Environmental Review',
    residential: 'Basic disclosure',
    commercial: 'Phase I ESA mandatory',
    notes: 'Environmental liability is major concern for commercial properties'
  },
  {
    aspect: 'Survey Requirements',
    residential: 'Often waived',
    commercial: 'ALTA survey required',
    notes: 'Boundary precision critical for commercial use and financing'
  },
  {
    aspect: 'Title Insurance Limits',
    residential: '$200K - $1M typical',
    commercial: '$1M - $50M+',
    notes: 'Higher policy limits with specialized endorsements'
  },
  {
    aspect: 'Zoning Compliance',
    residential: 'Basic verification',
    commercial: 'Comprehensive analysis',
    notes: 'Current use, future expansion, parking, density restrictions'
  }
];

const dueDiligenceAreas = [
  {
    category: 'Financial Due Diligence',
    description: 'Comprehensive financial analysis of the property',
    timeline: '30-60 days',
    keyItems: [
      'Rent rolls and lease abstracts for past 3 years',
      'Operating expense statements and budgets', 
      'Property tax assessments and appeals history',
      'Capital expenditure records and projections',
      'Property management agreements and fees',
      'Utility expenses and service contracts'
    ],
    professionals: ['CPA', 'Property Manager', 'Real Estate Appraiser'],
    risks: ['Hidden expenses', 'Inflated income', 'Deferred maintenance costs']
  },
  {
    category: 'Legal Documentation Review',
    description: 'Examination of all property-related legal documents',
    timeline: '45-75 days',
    keyItems: [
      'All current leases and amendments',
      'Service contracts and vendor agreements',
      'Insurance policies and claims history',
      'Litigation history and pending matters',
      'Permits, licenses, and compliance certificates',
      'Easements, restrictions, and encumbrances'
    ],
    professionals: ['Real Estate Attorney', 'Title Agent', 'Compliance Specialist'],
    risks: ['Undisclosed obligations', 'Non-conforming use', 'Pending litigation']
  },
  {
    category: 'Physical Property Inspection',
    description: 'Comprehensive condition assessment',
    timeline: '30-45 days',
    keyItems: [
      'Structural engineering evaluation',
      'Mechanical, electrical, plumbing systems',
      'Roof condition and remaining useful life',
      'HVAC system capacity and efficiency',
      'Americans with Disabilities Act compliance',
      'Environmental hazards assessment'
    ],
    professionals: ['Structural Engineer', 'MEP Engineer', 'Environmental Consultant'],
    risks: ['Major structural issues', 'System failures', 'Code violations']
  },
  {
    category: 'Market & Zoning Analysis',
    description: 'Property use and market position evaluation',
    timeline: '20-30 days',
    keyItems: [
      'Current zoning classification and permitted uses',
      'Variance and special use permit history',
      'Future development plans in area',
      'Comparable property analysis',
      'Market rent and vacancy trends',
      'Traffic patterns and accessibility'
    ],
    professionals: ['Zoning Attorney', 'Market Analyst', 'Urban Planner'],
    risks: ['Use restrictions', 'Market decline', 'Zoning changes']
  }
];

const commercialEndorsements = [
  { 
    name: 'ALTA 3.1 - Zoning', 
    purpose: 'Confirms current zoning and use compliance',
    cost: '$250-500',
    whenNeeded: 'All commercial properties'
  },
  { 
    name: 'ALTA 4 - Condominium', 
    purpose: 'Coverage for condominium units and common areas',
    cost: '$150-300',
    whenNeeded: 'Commercial condominiums'
  },
  { 
    name: 'ALTA 8.1 - Environmental Protection', 
    purpose: 'Protection against environmental liens',
    cost: '$200-400',
    whenNeeded: 'Properties with environmental risk'
  },
  { 
    name: 'ALTA 9 - Restrictions/Encroachments', 
    purpose: 'Comprehensive encroachment coverage',
    cost: '$300-600',
    whenNeeded: 'Properties with boundary issues'
  },
  { 
    name: 'ALTA 13 - Leasehold', 
    purpose: 'Protection for leasehold estates',
    cost: '$400-800',
    whenNeeded: 'Ground lease properties'
  },
  { 
    name: 'ALTA 17 - Access and Entry', 
    purpose: 'Confirms legal access to property',
    cost: '$200-350',
    whenNeeded: 'Properties with access issues'
  }
];

const CommercialTitleProcess: React.FC = () => {
  const [expandedSection, setExpandedSection] = useState<string | false>(false);

  const handleSectionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  return (
    <>
      <Helmet>
        <title>Commercial Real Estate Title Process | Expert Guide | MAD Title Agency</title>
        <meta
          name="description"
          content="Expert guide to commercial real estate title processes in New Jersey. Comprehensive due diligence, complex transactions, and professional services for commercial properties."
        />
        <meta
          name="keywords"
          content="commercial real estate title, commercial title insurance, commercial due diligence, commercial property New Jersey, ALTA survey, commercial title process, Middlesex County commercial"
        />
      </Helmet>

      {/* Breadcrumb */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 2 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link> →{' '}
            <Link to="/resources" style={{ textDecoration: 'none', color: 'inherit' }}>Resources</Link> →{' '}
            Commercial Title Process
          </Typography>
        </Container>
      </Box>

      {/* Header */}
      <Box sx={{ backgroundColor: '#1e40af', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Chip
                label="Commercial Expertise"
                sx={{
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  mb: 2,
                }}
              />
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                }}
              >
                Commercial Real Estate Title Process
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
                Expert guide to navigating complex commercial transactions in New Jersey
              </Typography>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{
                  backgroundColor: '#f59e0b',
                  '&:hover': { backgroundColor: '#d97706' },
                }}
              >
                Download Commercial Guide
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                }}
              >
                <BusinessIcon sx={{ fontSize: 64, mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Commercial Specialist
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Complex Transaction Expert
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Introduction */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e40af' }}>
            Why Commercial Title Work is Different
          </Typography>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7 }}>
                Commercial real estate transactions are significantly more complex than residential deals, 
                requiring specialized expertise in business law, environmental regulations, zoning compliance, 
                and sophisticated financial structures. The stakes are higher, timelines are longer, and the 
                due diligence process is exponentially more detailed.
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7 }}>
                At MAD Title Agency, we specialize in navigating these complex transactions, providing the 
                expertise and attention to detail that commercial properties demand. Our team understands 
                the unique challenges of commercial real estate in Middlesex County and throughout New Jersey.
              </Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Commercial Advantage:</strong> Our experienced team has closed over $500M in 
                  commercial transactions, from small retail properties to major industrial developments.
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, backgroundColor: '#f0f9ff' }}>
                <TrendingUpIcon sx={{ fontSize: 48, color: '#1e40af', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Commercial Complexity
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Extended due diligence periods" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Multiple stakeholder coordination" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Complex financing structures" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Specialized insurance requirements" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Process Comparison */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e40af' }}>
            Commercial vs. Residential Process Comparison
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell><strong>Process Element</strong></TableCell>
                  <TableCell><strong>Residential</strong></TableCell>
                  <TableCell><strong>Commercial</strong></TableCell>
                  <TableCell><strong>Key Differences</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {commercialVsResidential.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {item.aspect}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.residential}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af' }}>
                        {item.commercial}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {item.notes}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Due Diligence Process */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e40af' }}>
            Comprehensive Due Diligence Process
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
            Commercial transactions require thorough investigation across multiple categories:
          </Typography>

          {dueDiligenceAreas.map((area, index) => (
            <Accordion
              key={index}
              expanded={expandedSection === `area${index}`}
              onChange={handleSectionChange(`area${index}`)}
              sx={{ mb: 2, border: '1px solid #e2e8f0' }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {area.category}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {area.description}
                    </Typography>
                  </Box>
                  <Chip
                    label={area.timeline}
                    size="small"
                    sx={{
                      backgroundColor: '#1e40af',
                      color: 'white',
                      mr: 2,
                    }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#16a34a' }}>
                      Key Investigation Items:
                    </Typography>
                    <List>
                      {area.keyItems.map((item, itemIndex) => (
                        <ListItem key={itemIndex}>
                          <ListItemIcon>
                            <CheckIcon sx={{ color: '#16a34a' }} />
                          </ListItemIcon>
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1e40af' }}>
                      Professional Team:
                    </Typography>
                    <List>
                      {area.professionals.map((professional, profIndex) => (
                        <ListItem key={profIndex}>
                          <ListItemIcon>
                            <BusinessIcon sx={{ color: '#1e40af' }} />
                          </ListItemIcon>
                          <ListItemText primary={professional} />
                        </ListItem>
                      ))}
                    </List>

                    <Typography variant="h6" sx={{ mb: 2, mt: 3, fontWeight: 600, color: '#dc2626' }}>
                      Common Risk Areas:
                    </Typography>
                    <List>
                      {area.risks.map((risk, riskIndex) => (
                        <ListItem key={riskIndex}>
                          <ListItemIcon>
                            <WarningIcon sx={{ color: '#dc2626' }} />
                          </ListItemIcon>
                          <ListItemText primary={risk} />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Commercial Title Insurance */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e40af' }}>
            Commercial Title Insurance & Endorsements
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
            Commercial properties typically require specialized ALTA endorsements for comprehensive protection:
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell><strong>Endorsement</strong></TableCell>
                  <TableCell><strong>Purpose</strong></TableCell>
                  <TableCell><strong>Typical Cost</strong></TableCell>
                  <TableCell><strong>When Needed</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {commercialEndorsements.map((endorsement, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {endorsement.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{endorsement.purpose}</TableCell>
                    <TableCell>{endorsement.cost}</TableCell>
                    <TableCell>{endorsement.whenNeeded}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* MAD Title Commercial Expertise */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e40af' }}>
            MAD Title Commercial Expertise
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, height: '100%', backgroundColor: '#f0fdf4' }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  ✅ Our Commercial Experience
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="$500M+ in Commercial Closings"
                      secondary="Proven track record with major transactions"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Multi-State Coordination"
                      secondary="Complex portfolio and development projects"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Expedited Processing"
                      secondary="Dedicated commercial team for faster turnaround"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Institutional Lender Network"
                      secondary="Established relationships with major commercial lenders"
                    />
                  </ListItem>
                </List>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, height: '100%', backgroundColor: '#f0f9ff' }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  🏢 Property Types We Handle
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Office Buildings & Business Parks"
                      secondary="From small professional buildings to corporate headquarters"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Retail & Shopping Centers"
                      secondary="Strip malls, anchored centers, and standalone retail"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Industrial & Warehouse"
                      secondary="Manufacturing, distribution, and logistics facilities"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Multi-Family Investment"
                      secondary="Apartment complexes and residential investment properties"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Mixed-Use Developments"
                      secondary="Complex projects combining multiple property types"
                    />
                  </ListItem>
                </List>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Process Timeline */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e40af' }}>
            Typical Commercial Transaction Timeline
          </Typography>
          <Paper sx={{ p: 4 }}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={3}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <ScheduleIcon sx={{ fontSize: 48, color: '#1e40af', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Days 1-30
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Initial due diligence, contract execution, title commitment issued
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <SearchIcon sx={{ fontSize: 48, color: '#059669', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Days 30-90
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Comprehensive due diligence, inspections, environmental assessment
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <BankIcon sx={{ fontSize: 48, color: '#f59e0b', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Days 90-120
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Financing finalization, final approvals, closing preparation
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <DocumentIcon sx={{ fontSize: 48, color: '#16a34a', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Closing Day
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Final documentation, funding, recording, keys transfer
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* Related Resources */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e40af' }}>
            Related Professional Resources
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <BankIcon sx={{ fontSize: 48, color: '#1e40af', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Rate Calculator
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Calculate title insurance premiums for commercial properties.
                </Typography>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/resources/nj-title-rate-calculator"
                  fullWidth
                >
                  Calculate Rates
                </Button>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <SearchIcon sx={{ fontSize: 48, color: '#1e40af', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Title Search Manual
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Professional procedures for comprehensive title examination.
                </Typography>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/resources/title-search-manual"
                  fullWidth
                >
                  View Manual
                </Button>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <LegalIcon sx={{ fontSize: 48, color: '#1e40af', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Title Defects Guide
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Common issues and resolution strategies for complex properties.
                </Typography>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/resources/title-defects-guide"
                  fullWidth
                >
                  View Guide
                </Button>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 2, fontWeight: 600, color: '#64748b' }}>
            Ready for Your Commercial Transaction?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
            Our commercial title experts handle complex transactions with precision and expertise.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              component={Link}
              to="/contact"
              sx={{
                background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
              }}
            >
              Start Your Commercial Transaction
            </Button>
            <Button
              variant="outlined"
              size="large"
              href="mailto:madtitleagency@gmail.com"
              sx={{
                borderColor: '#1e40af',
                color: '#1e40af',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
              }}
            >
              Commercial Services Team
            </Button>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default CommercialTitleProcess; 